/**
 * ExecutionMetadata — Key-value metadata display for an execution.
 * Renders the top-level fields returned by POST /executions/fetch.
 */
import Badge from '@cloudscape-design/components/badge';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { ExecutionResponse } from 'src/store/slices/execution/executionState';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';
import { CopyableText } from 'src/components/common/CopyableText';

interface ExecutionMetadataProps {
  data: ExecutionResponse;
}

/** Maps a beacon state string to a Cloudscape StatusIndicator type. */
const stateToIndicatorType = (state: string | null): 'success' | 'error' | 'in-progress' => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  return 'in-progress';
};

export const ExecutionMetadata = ({ data }: ExecutionMetadataProps) => (
  <KeyValuePairs
    columns={2}
    items={[
      { label: 'Execution ID', value: <CopyableText value={data.id} /> },
      { label: 'Type', value: <Badge>{data.executionType}</Badge> },
      { label: 'Plugin', value: data.pluginName || '—' },
      { label: 'Data Source', value: data.dataSource ? <Badge>{data.dataSource}</Badge> : '—' },
      { label: 'State', value: (
        <StatusIndicator type={stateToIndicatorType(data.beaconExecutionState)}>
          {data.beaconExecutionState || 'UNKNOWN'}
        </StatusIndicator>
      )},
      { label: 'Status', value: data.beaconExecutionStatus || '—' },
      { label: 'Tags', value: data.executionTags?.join(', ') || '—' },
      { label: 'Total Latency', value: formatLatency(data.totalLatencyMs) },
      { label: 'Created', value: formatIsoTimestamp(data.createdAt) },
      { label: 'Updated', value: formatIsoTimestamp(data.updatedAt) },
      { label: 'Payments', value: String(data.paymentIds?.length ?? 0) },
    ]}
  />
);
